import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import moment from "moment";
import Datetime from "react-datetime";
import { getPartnerStoresWithStaggering, getStoreRides, generateStoreToken, sendUrlEmail } from "../../helpers/apiHelper";
import { buildSchedule } from "./components/scheduleUtils";
import ScheduleTable from "./components/ScheduleTable";
import RideModal from "./components/RideModal";
import ShareModal from "./components/ShareModal";
import "./DispatchOverview.css";

const DispatchOverview = ({ impersonated_partner }) => {
  const { t, i18n } = useTranslation();
  // moment's global locale is forced to 'fr' by `require('moment/locale/fr')` in
  // dateHelper, so the calendar ignores the app language. Drive it off i18n instead.
  const dateLocale = i18n.language && i18n.language.includes("fr") ? "fr" : "en";
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(
    () => localStorage.getItem("dispatchOverview_selectedStoreId") || ""
  );
  const [selectedDate, setSelectedDate] = useState(() => moment().startOf("day"));
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRide, setSelectedRide] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [copied, setCopied] = useState(false);

  const isMountedRef = useRef(true);
  const ridesCacheRef = useRef({});
  const tokenCacheRef = useRef({});
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      setError("");
      const partnerId = impersonated_partner ? impersonated_partner.partner_id : null;
      try {
        const storesRes = await getPartnerStoresWithStaggering(partnerId);
        if (!isMountedRef.current) return;
        setStores(storesRes || []);
      } catch (err) {
        if (isMountedRef.current) {
          setError("Failed to load stores.");
          console.error(err);
        }
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    fetchStores();
    const interval = setInterval(fetchStores, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [impersonated_partner]);

  const fetchRides = useCallback(async () => {
    if (!selectedStoreId) return;
    const dateStr = selectedDate.format("YYYY-MM-DD");
    const cacheKey = `${selectedStoreId}_${dateStr}`;
    setRides(ridesCacheRef.current[cacheKey] || []);
    const partnerId = impersonated_partner ? impersonated_partner.partner_id : null;
    try {
      const ridesRes = await getStoreRides(selectedStoreId, dateStr, partnerId);
      if (!isMountedRef.current) return;
      const fetched = ridesRes || [];
      ridesCacheRef.current[cacheKey] = fetched;
      setRides(fetched);
      setLastRefreshed(new Date());
    } catch (err) {
      if (isMountedRef.current) {
        setError("Failed to load rides.");
        console.error(err);
      }
    }
  }, [selectedStoreId, selectedDate, impersonated_partner]);

  useEffect(() => {
    fetchRides();
    const interval = setInterval(fetchRides, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchRides]);

  const selectedStore = stores.find((s) => String(s.store_id) === String(selectedStoreId));
  const schedule = buildSchedule(selectedStore, rides);
  const isReady = selectedStoreId && !loading;

  const handleShare = async () => {
    if (!selectedStore) return;
    if (tokenCacheRef.current[selectedStoreId]) {
      setShareUrl(tokenCacheRef.current[selectedStoreId]);
      setSendStatus(null);
      return;
    }
    setGeneratingLink(true);
    const partnerId = impersonated_partner ? impersonated_partner.partner_id : null;
    try {
      const token = await generateStoreToken(selectedStoreId, partnerId);
      const url = `${window.location.origin}/public/store_schedule?token=${token}`;
      tokenCacheRef.current[selectedStoreId] = url;
      setShareUrl(url);
      setSendStatus(null);
    } catch (err) {
      console.error(err);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendEmail = async () => {
    if (!selectedStore || !shareUrl) return;
    setSendStatus("sending");
    try {
      await sendUrlEmail(selectedStore.email, shareUrl, "View Schedule", selectedStore.name);
      setSendStatus("sent");
    } catch (err) {
      console.error(err);
      setSendStatus("error");
    }
  };

  const closeShareModal = () => { setShareUrl(null); setSendStatus(null); setCopied(false); };

  return (
    <div className="content">
      <div className="container-fluid dispatch-overview-shell">
        <div className="card">
          <div className="content">
            {lastRefreshed && (
              <p className="dispatch-overview-last-refreshed">
                {t("LAST_REFRESHED")}: {lastRefreshed.toLocaleTimeString()}
              </p>
            )}

            <h4 className="title">{t("SELECT_STORE")}</h4>

            {error && <p className="text-danger">{error}</p>}
            {!error && !loading && !stores.length && <p>{t("NO_STORES_AVAILABLE")}</p>}
            {loading && <p>{t("LOADING")}…</p>}

            {!loading && stores.length > 0 && (
              <div className="dispatch-overview-selector-row">
                <select
                  className="form-control dispatch-overview-store-select"
                  value={selectedStoreId}
                  onChange={(e) => {
                    setSelectedStoreId(e.target.value);
                    localStorage.setItem("dispatchOverview_selectedStoreId", e.target.value);
                  }}
                >
                  <option value="">{t("SELECT_STORE")}</option>
                  {stores.map((s) => (
                    <option key={s.store_id} value={s.store_id}>{s.name}</option>
                  ))}
                </select>
                <div className="dispatch-overview-actions-row">
                  {selectedStore && (
                    <button
                      className="dispatch-overview-share-btn"
                      onClick={handleShare}
                      disabled={generatingLink}
                    >
                      {generatingLink ? t("GENERATING_LINK") : t("SHARE_WITH_STORE")}
                    </button>
                  )}

                  <div className="dispatch-overview-date-wrapper">
                    <Datetime
                      onChange={(val) => {
                        if (moment.isMoment(val)) setSelectedDate(val.startOf("day"));
                      }}
                      value={selectedDate}
                      dateFormat="YYYY/MM/DD"
                      timeFormat={false}
                      closeOnSelect
                      locale={dateLocale}
                      className="dispatch-overview-datetime"
                      inputProps={{
                        readOnly: true,
                        style: { cursor: "pointer" },
                        className: "form-control dispatch-overview-date-input",
                      }}
                    />
                    <i className="fa fa-calendar dispatch-overview-date-icon" />
                  </div>
                </div>
              </div>
            )}

            {isReady && <ScheduleTable schedule={schedule} onRideClick={setSelectedRide} />}
          </div>
        </div>
      </div>

      <ShareModal
        store={selectedStore}
        shareUrl={shareUrl}
        onClose={closeShareModal}
        copied={copied}
        onCopy={handleCopy}
        sendStatus={sendStatus}
        onSend={handleSendEmail}
      />

      <RideModal ride={selectedRide} onClose={() => setSelectedRide(null)} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  impersonated_partner: state.partners.impersonated_partner,
});

export default connect(mapStateToProps)(DispatchOverview);
